import { CuriosityAutocomplete, CuriosityNamespace, CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Operator } from '@procureai/query-builder';
import {
  ValueEditorDate,
  ValueEditorDateRange,
  ValueEditorInput,
  ValueEditorNumberInput,
  ValueEditorNumberRange,
  ValueEditorSelect,
  ValueEditorSwitch,
} from '@procureai/query-builder-ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SavingReductionType, SavingStatus, SavingType } from '@/generated/gql/graphql';
import {
  listOperators,
  operators,
  queryFieldFactory,
  UseDataGridQueryBuilderConfig,
} from '~/components/DataGridQueryBuilder';
import {
  ValueEditorSelectAutocomplete,
  ValueEditorSelectAutocompleteProps,
} from '~/components/ValueEditorSelectAutocomplete/ValueEditorSelectAutocomplete';

const SavingFilterField = {
  id: 'id',
  name: 'name',
  description: 'description',
  freeTextCommodity: 'freeTextCommodity',
  status: 'status',
  savingType: 'savingType',
  currency: 'currency',
  reductionType: 'reductionType',
  baselinePrice: 'baselinePrice',
  newPrice: 'newPrice',
  baselineQuantity: 'baselineQuantity',
  newQuantity: 'newQuantity',
  validFrom: 'validFrom',
  validTo: 'validTo',
  multiYearSaving: 'multiYearSaving',
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
  fkBuyer: 'fkBuyer',
  fkSupplier: 'fkSupplier',
  fkCommodityGroup: 'fkCommodityGroup',
  fkProcurementOrganization: 'fkProcurementOrganization',
  fkOrganization: 'fkOrganization',
  fkPurchasingGroup: 'fkPurchasingGroup',
  fkAccountingArea: 'fkAccountingArea',
  fkUnit: 'fkUnit',
  fkCreatedBy: 'fkCreatedBy',
  fkUpdatedBy: 'fkUpdatedBy',
  sourceReference: 'sourceReference',
  sourceReferenceItem: 'sourceReferenceItem',
  sourceSystem: 'sourceSystem',
  costAllocationValue: 'costAllocationValue',
  costAllocationGlAccount: 'costAllocationGlAccount',
} as const;

enum SavingQueryFieldGroup {
  GENERAL = 'general',
  PRICING = 'pricing',
  DATES = 'dates',
  RELATIONS = 'relations',
  REFERENCES = 'references',
}

const curiosityNsMap: Partial<Record<string, CuriosityNamespace>> = {
  [SavingFilterField.fkBuyer]: CuriosityNamespaceType.INTERNAL_USER,
  [SavingFilterField.fkSupplier]: CuriosityNamespaceType.SUPPLIER,
  [SavingFilterField.fkCommodityGroup]: CuriosityNamespaceType.COMMODITY_GROUP,
  [SavingFilterField.fkProcurementOrganization]: CuriosityNamespaceType.PROCUREMENT_ORGANIZATION,
  [SavingFilterField.fkOrganization]: CuriosityNamespaceType.ORGANIZATION,
  [SavingFilterField.fkPurchasingGroup]: CuriosityNamespaceType.PURCHASING_GROUP,
  [SavingFilterField.fkAccountingArea]: CuriosityNamespaceType.ACCOUNTING_AREA,
  [SavingFilterField.fkUnit]: CuriosityNamespaceType.UNIT,
  [SavingFilterField.fkCreatedBy]: CuriosityNamespaceType.INTERNAL_USER,
  [SavingFilterField.fkUpdatedBy]: CuriosityNamespaceType.INTERNAL_USER,
  [SavingFilterField.costAllocationGlAccount]: CuriosityNamespaceType.GL_ACCOUNT,
  [SavingFilterField.sourceReferenceItem]: CuriosityNamespaceType.PURCHASE_REQUISITION_ITEM,
};

const enumAutocompleteMap: Record<
  string,
  Pick<ValueEditorSelectAutocompleteProps, 'initialI18nPrefix' | 'initialOptions'>
> = {
  [SavingFilterField.status]: {
    initialI18nPrefix: 'SavingStatus',
    initialOptions: Object.keys(SavingStatus),
  },
  [SavingFilterField.savingType]: {
    initialI18nPrefix: 'SavingType',
    initialOptions: Object.keys(SavingType),
  },
  [SavingFilterField.reductionType]: {
    initialI18nPrefix: 'SavingReductionType',
    initialOptions: Object.keys(SavingReductionType),
  },
};

export const useSavingsDataGridQueryBuilderConfig: UseDataGridQueryBuilderConfig = () => {
  const { t } = useTranslation();

  return useMemo(() => {
    const createField = queryFieldFactory(t, { i18nPrefix: 'SavingQueryBuilder' });
    return {
      fields: [
        createField({
          name: SavingFilterField.id,
          grp: SavingQueryFieldGroup.GENERAL,
          operators: operators.id,
        }),
        createField({
          name: SavingFilterField.name,
          grp: SavingQueryFieldGroup.GENERAL,
          operators: operators.text,
        }),
        createField({
          name: SavingFilterField.description,
          grp: SavingQueryFieldGroup.GENERAL,
          operators: operators.text,
        }),
        createField({
          name: SavingFilterField.freeTextCommodity,
          grp: SavingQueryFieldGroup.GENERAL,
          operators: operators.text,
        }),
        createField({
          name: SavingFilterField.status,
          grp: SavingQueryFieldGroup.GENERAL,
          operators: operators.enumValueNotEmpty,
        }),
        createField({
          name: SavingFilterField.savingType,
          grp: SavingQueryFieldGroup.GENERAL,
          operators: operators.enumValueNotEmpty,
        }),
        createField({
          name: SavingFilterField.currency,
          grp: SavingQueryFieldGroup.GENERAL,
          operators: operators.textAuto,
        }),
        createField({
          name: SavingFilterField.reductionType,
          grp: SavingQueryFieldGroup.GENERAL,
          operators: operators.enumValueNotEmpty,
        }),
        createField({
          name: SavingFilterField.baselinePrice,
          grp: SavingQueryFieldGroup.PRICING,
          operators: operators.num,
        }),
        createField({
          name: SavingFilterField.newPrice,
          grp: SavingQueryFieldGroup.PRICING,
          operators: operators.num,
        }),
        createField({
          name: SavingFilterField.baselineQuantity,
          grp: SavingQueryFieldGroup.PRICING,
          operators: operators.num,
        }),
        createField({
          name: SavingFilterField.newQuantity,
          grp: SavingQueryFieldGroup.PRICING,
          operators: operators.num,
        }),
        createField({
          name: SavingFilterField.validFrom,
          grp: SavingQueryFieldGroup.DATES,
          operators: operators.date,
        }),
        createField({
          name: SavingFilterField.validTo,
          grp: SavingQueryFieldGroup.DATES,
          operators: operators.date,
        }),
        createField({
          name: SavingFilterField.multiYearSaving,
          grp: SavingQueryFieldGroup.DATES,
          operators: operators.single,
        }),
        createField({
          name: SavingFilterField.createdAt,
          grp: SavingQueryFieldGroup.DATES,
          operators: operators.date,
        }),
        createField({
          name: SavingFilterField.updatedAt,
          grp: SavingQueryFieldGroup.DATES,
          operators: operators.date,
        }),
        createField({
          name: SavingFilterField.fkBuyer,
          grp: SavingQueryFieldGroup.RELATIONS,
          operators: operators.idAuto,
        }),
        createField({
          name: SavingFilterField.fkSupplier,
          grp: SavingQueryFieldGroup.RELATIONS,
          operators: operators.idAuto,
        }),
        createField({
          name: SavingFilterField.fkCommodityGroup,
          grp: SavingQueryFieldGroup.RELATIONS,
          operators: operators.idAuto,
        }),
        createField({
          name: SavingFilterField.fkProcurementOrganization,
          grp: SavingQueryFieldGroup.RELATIONS,
          operators: operators.idAuto,
        }),
        createField({
          name: SavingFilterField.fkOrganization,
          grp: SavingQueryFieldGroup.RELATIONS,
          operators: operators.idAuto,
        }),
        createField({
          name: SavingFilterField.fkPurchasingGroup,
          grp: SavingQueryFieldGroup.RELATIONS,
          operators: operators.idAuto,
        }),
        createField({
          name: SavingFilterField.fkAccountingArea,
          grp: SavingQueryFieldGroup.RELATIONS,
          operators: operators.idAuto,
        }),
        createField({
          name: SavingFilterField.fkUnit,
          grp: SavingQueryFieldGroup.RELATIONS,
          operators: operators.idAuto,
        }),
        createField({
          name: SavingFilterField.fkCreatedBy,
          grp: SavingQueryFieldGroup.RELATIONS,
          operators: operators.idAuto,
        }),
        createField({
          name: SavingFilterField.fkUpdatedBy,
          grp: SavingQueryFieldGroup.RELATIONS,
          operators: operators.idAuto,
        }),
        createField({
          name: SavingFilterField.sourceReference,
          grp: SavingQueryFieldGroup.REFERENCES,
          operators: operators.relationText,
        }),
        createField({
          name: SavingFilterField.sourceReferenceItem,
          grp: SavingQueryFieldGroup.REFERENCES,
          operators: operators.relationText,
        }),
        createField({
          name: SavingFilterField.sourceSystem,
          grp: SavingQueryFieldGroup.REFERENCES,
          operators: operators.relationText,
        }),
        createField({
          name: SavingFilterField.costAllocationValue,
          grp: SavingQueryFieldGroup.REFERENCES,
          operators: operators.relationText,
        }),
        createField({
          name: SavingFilterField.costAllocationGlAccount,
          grp: SavingQueryFieldGroup.REFERENCES,
          operators: operators.idAuto,
        }),
      ],
      getOperatorLabel: ({ field, operator }) => {
        switch (field) {
          case SavingFilterField.validFrom:
          case SavingFilterField.validTo:
          case SavingFilterField.createdAt:
          case SavingFilterField.updatedAt:
            if (operator === Operator.LT || operator === Operator.GT) {
              return `QueryBuilder.operatorDate.${operator}`;
            }
        }
      },
      renderValueEditor: ({ field, operator, ...otherProps }) => {
        if (operator === Operator.EMPTY || operator === Operator.NOT_EMPTY) {
          return null;
        }

        switch (field) {
          case SavingFilterField.id:
          case SavingFilterField.name:
          case SavingFilterField.description:
          case SavingFilterField.freeTextCommodity:
          case SavingFilterField.sourceReference:
          case SavingFilterField.sourceSystem:
          case SavingFilterField.costAllocationValue:
            if (listOperators[operator]) {
              return <ValueEditorSelect {...otherProps} multiple freeSolo />;
            }
            return <ValueEditorInput {...otherProps} />;
          case SavingFilterField.status:
          case SavingFilterField.savingType:
          case SavingFilterField.reductionType: {
            const enumOptions = enumAutocompleteMap[field];
            if (enumOptions) {
              return (
                <ValueEditorSelectAutocomplete {...otherProps} {...enumOptions} multiple={listOperators[operator]} />
              );
            }
            return null;
          }
          case SavingFilterField.currency:
            return <ValueEditorSelect {...otherProps} multiple={listOperators[operator]} freeSolo />;
          case SavingFilterField.baselinePrice:
          case SavingFilterField.newPrice:
          case SavingFilterField.baselineQuantity:
          case SavingFilterField.newQuantity:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorNumberRange {...otherProps} />;
            }
            return <ValueEditorNumberInput min={0} {...otherProps} />;
          case SavingFilterField.validFrom:
          case SavingFilterField.validTo:
          case SavingFilterField.createdAt:
          case SavingFilterField.updatedAt:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorDateRange {...otherProps} />;
            }
            return <ValueEditorDate {...otherProps} />;
          case SavingFilterField.multiYearSaving:
            return <ValueEditorSwitch {...otherProps} />;
          case SavingFilterField.fkBuyer:
          case SavingFilterField.fkSupplier:
          case SavingFilterField.fkCommodityGroup:
          case SavingFilterField.fkProcurementOrganization:
          case SavingFilterField.fkOrganization:
          case SavingFilterField.fkPurchasingGroup:
          case SavingFilterField.fkAccountingArea:
          case SavingFilterField.fkUnit:
          case SavingFilterField.fkCreatedBy:
          case SavingFilterField.fkUpdatedBy:
          case SavingFilterField.costAllocationGlAccount:
          case SavingFilterField.sourceReferenceItem: {
            const namespace = curiosityNsMap[field];
            if (namespace) {
              return (
                <CuriosityAutocomplete
                  ns={namespace}
                  multiple={listOperators[operator]}
                  simplifyValue
                  {...otherProps}
                />
              );
            }
            return null;
          }
          default:
            return null;
        }
      },
    };
  }, [t]);
};
